import { appConfig } from '../../helpers/app-config/app-config';
import type { TaskTypeValue } from '../../containers/tasks-table/tasks-table.type';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import type { GraphData, ProcessMapping, ProcessTasksData, ProcessTaskTypesData } from './process-client.type';
import { GraphType } from './process-client.type';

const axiosInstance = AxiosInstance();
const baseURL = appConfig.VITE_APP_API_URL + '/api/process/';

/**
 * Fetches the process graph data
 *
 * @param {string} project_id - The ID of the project
 * @param {string} subproject_id - The ID of the subproject (optional)
 * @param {string} work_period_id - The ID of the work period (optional)
 * @param {TaskType[]} task_types - The types of tasks (optional, comma-separated on API)
 * @param {string} epic_id - The ID of the epic (optional)
 * @param {GraphType} graph_type - The type of graph (optional)
 * @param {string} start_date - The start date (optional)
 * @param {string} end_date - The end date (optional)
 * @return {Promise<GraphData>} - A Promise that resolves to the process graph data
 */
const getProcessGraph = ({
  project_id,
  workflow_id,
  subproject_id,
  work_period_id,
  task_types,
  epic_id,
  task_id,
  graph_type,
  start_date,
  end_date,
  bloomfilter_sections,
}: {
  project_id?: string;
  workflow_id?: string;
  subproject_id?: string;
  work_period_id?: string;
  task_types?: TaskTypeValue[] | null;
  epic_id?: string;
  task_id?: string;
  graph_type?: GraphType;
  start_date?: string;
  end_date?: string;
  bloomfilter_sections?: boolean;
}): Promise<GraphData> => {
  const url = `${baseURL}graph/`;

  // Only require project_id if workflow_id is not provided
  if (!project_id && !workflow_id) {
    return Promise.reject('Either project_id or workflow_id is required');
  }

  const params: Record<string, any> = {
    project_id,
    workflow_id,
    subproject_id,
    work_period_id,
    epic_id,
    task_id,
    graph_type,
    bloomfilter_sections,
  };

  // Only include start_date and end_date if work_period_id is not provided
  if (!work_period_id) {
    params.start_date = start_date;
    params.end_date = end_date;
  }

  // Convert task_type array to comma-separated string, only add if not empty
  if (task_types?.length) {
    params.task_types = task_types.join(',');
  }

  return axiosInstance
    .get(url, { params })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching process graph'));
};

/**
 * Fetches tasks for the process map
 *
 * @param {string} project_id - The ID of the project
 * @param {string} workflow_id - The ID of the workflow (null if not specified)
 * @param {string} subproject_id - The ID of the subproject (null if not specified)
 * @param {string} work_period_id - The ID of the work period (optional)
 * @param {TaskType[]} task_types - The types of tasks (null if not specified, comma-separated on API)
 * @param {string} epic_id - The ID of the epic (optional)
 * @param {string} start_date - The start date (optional)
 * @param {string} end_date - The end date (optional)
 * @param {string} subgraph_path - The subgraph path (optional)
 * @return {Promise<ProcessTasksData>} - A Promise that resolves to the process tasks data
 */
const getProcessTasks = (
  project_id: string | null,
  workflow_id: string | null,
  subproject_id: string | null,
  work_period_id: string | null,
  task_types: TaskTypeValue[] | null,
  epic_id: string | null,
  start_date?: string,
  end_date?: string,
  subgraph_path?: string,
): Promise<ProcessTasksData> => {
  const url = `${baseURL}tasks/`;
  const params: Record<string, any> = {
    project_id,
    workflow_id,
    subproject_id,
    work_period_id,
    epic_id,
    subgraph_path,
  };

  // Only include start_date and end_date if work_period_id is not provided
  if (!work_period_id) {
    params.start_date = start_date;
    params.end_date = end_date;
  }

  // Convert task_type array to comma-separated string, only add if not empty
  if (task_types?.length) {
    params.task_types = task_types.join(',');
  }

  return axiosInstance
    .get(url, { params })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching process tasks'));
};

/**
 * Fetch the process stages mapping for a process map
 *
 * @param {string} id - the ID of the project or workflow
 * @param {boolean} isWorkflow - whether the ID is a workflow ID (optional, default is false)
 * @return {Promise<ProcessMapping>} - A Promise that resolves to the process mapping data
 */
const getProcessMapping = (id: string, isWorkflow = false): Promise<ProcessMapping> => {
  const url = `${appConfig.VITE_APP_API_URL}/api/${isWorkflow ? 'workflows' : 'projects'}/${id}/external-section-map/`;

  return axiosInstance
    .get(url)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching process mapping'));
};

/**
 * Fetches the distinct work item types present in the scoped task set.
 * Fires without date filtering so the dropdown can be populated quickly,
 * in parallel with the full graph request.
 *
 * @param {string} project_id - The ID of the project (required if workflow_id not provided)
 * @param {string} workflow_id - The ID of the workflow (required if project_id not provided)
 * @param {string} subproject_id - The ID of the subproject (optional)
 * @param {string} work_period_id - The ID of the work period (optional)
 * @return {Promise<string[]>} - A Promise resolving to a sorted list of distinct raw_type strings
 */
const getProcessTaskTypes = ({
  project_id,
  workflow_id,
  subproject_id,
  work_period_id,
}: {
  project_id?: string;
  workflow_id?: string;
  subproject_id?: string;
  work_period_id?: string;
}): Promise<string[]> => {
  if (!project_id && !workflow_id) {
    return Promise.reject('Either project_id or workflow_id is required');
  }

  const url = `${baseURL}task-types/`;
  return axiosInstance
    .get(url, { params: { project_id, workflow_id, subproject_id, work_period_id } })
    .then((response) => response.data?.task_types ?? [])
    .catch((error) => {
      console.error(
        `Failed to fetch task types for project_id=${project_id} workflow_id=${workflow_id}: ${error?.message || error}`,
      );
      return [];
    });
};

/**
 * Fetches the distinct work item types in the scoped task set together with
 * their labeled options, from the same single endpoint call used by
 * `getProcessTaskTypes`. `taskTypes` mirrors the legacy `task_types` list;
 * `taskTypeOptions` carries `{ value, label }` pairs where `value` is the
 * exact stored raw_type (the graph `task_types` filter key) and `label` is
 * the verbatim external casing, equal to `value` when no label is synced.
 *
 * @param {string} project_id - The ID of the project (required if workflow_id not provided)
 * @param {string} workflow_id - The ID of the workflow (required if project_id not provided)
 * @param {string} subproject_id - The ID of the subproject (optional)
 * @param {string} work_period_id - The ID of the work period (optional)
 * @return {Promise<ProcessTaskTypesData>} - A Promise resolving to the raw type list and its labeled options
 */
const getProcessTaskTypesWithOptions = ({
  project_id,
  workflow_id,
  subproject_id,
  work_period_id,
}: {
  project_id?: string;
  workflow_id?: string;
  subproject_id?: string;
  work_period_id?: string;
}): Promise<ProcessTaskTypesData> => {
  if (!project_id && !workflow_id) {
    return Promise.reject('Either project_id or workflow_id is required');
  }

  const url = `${baseURL}task-types/`;
  return axiosInstance
    .get(url, { params: { project_id, workflow_id, subproject_id, work_period_id } })
    .then((response) => ({
      taskTypes: response.data?.task_types ?? [],
      taskTypeOptions: response.data?.task_type_options ?? [],
    }))
    .catch((error) => {
      console.error(
        `Failed to fetch task types for project_id=${project_id} workflow_id=${workflow_id}: ${error?.message || error}`,
      );
      return { taskTypes: [], taskTypeOptions: [] };
    });
};

export { getProcessGraph, getProcessMapping, getProcessTaskTypes, getProcessTaskTypesWithOptions, getProcessTasks };
